import { ChevronDown, ChevronRight, Code, Cog, Database, FileText, Image, Trash2, Upload } from "lucide-react";
import { FC, KeyboardEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "../../lib/utils";
import { filesystemService } from "../../utils/filesystem";

interface FileItem {
  path: string;
  name: string;
  type: "file" | "folder";
  size?: number;
  contents?: FileItem[];
}

interface FileExplorerProps {
  files: FileItem[];
  isCollapsed: boolean;
  onItemClick: (path: string, type: "file" | "folder") => void;
  onFilesUpdated?: () => void;
  activeFilePath?: string | null;
  onUploadFiles?: (event?: DragEvent) => void;
}

// Files to hide
const HIDDEN_FILES: string[] = [];

// Function to check if a file should be hidden
const shouldHideFile = (fileName: string): boolean => {
  return fileName.startsWith('.') || HIDDEN_FILES.includes(fileName);
};

// Function to get file type information
const getFileInfo = (fileName: string): { icon: JSX.Element, label?: string } => {
  // Special case for preswald.toml
  if (fileName === 'preswald.toml') {
    return {
      icon: <Cog className="fileexplorer-icon" />,
      label: 'Config'
    };
  }

  // Check file extensions
  if (fileName.endsWith('.py')) {
    return {
      icon: <Code className="fileexplorer-icon" />,
      label: 'Script'
    };
  }

  if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') ||
    fileName.endsWith('.png') || fileName.endsWith('.gif') ||
    fileName.endsWith('.svg') || fileName.endsWith('.webp') ||
    fileName.endsWith('.ico')
  ) {
    return {
      icon: <Image className="fileexplorer-icon" />,
      label: fileName
    };
  }

  if (fileName.endsWith('.csv')) {
    return {
      icon: <Database className="fileexplorer-icon" />,
      label: fileName
    };
  }

  if (fileName.endsWith('.md')) {
    return {
      icon: <FileText className="fileexplorer-icon" />,
      label: fileName
    };
  }

  // Default case
  return {
    icon: <FileText className="fileexplorer-icon" />,
    label: fileName
  };
};

// Simplified FileItem component
const FileItemComponent = memo(({
  file,
  isCollapsed,
  isActive,
  onOpen,
  onContextMenu
}: {
  file: FileItem;
  isCollapsed: boolean;
  isActive: boolean;
  onOpen: (file: FileItem) => void;
  onContextMenu: (e: React.MouseEvent, item?: FileItem) => void;
}) => {
  // Memoize file info to prevent recalculating on each render
  const fileInfo = useMemo(() => getFileInfo(file.name), [file.name]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(file);
    }
  };

  return (
    <div
      onClick={() => onOpen(file)}
      onContextMenu={(e) => onContextMenu(e, file)}
      onKeyDown={handleKeyDown}
      className={cn(
        "fileexplorer-file-button flex items-center gap-2 px-2 py-1.5 w-full hover:bg-gray-200 rounded-none",
        isActive && "fileexplorer-active-file",
        isCollapsed && "justify-center"
      )}
      title={file.name}
      tabIndex={0}
      role="button"
      aria-selected={isActive}
    >
      {fileInfo.icon}
      {!isCollapsed && <span className="truncate">{fileInfo.label}</span>}
    </div>
  );
});

FileItemComponent.displayName = 'FileItemComponent';

// Simplified FolderItem component
const FolderItemComponent = memo(({
  folder,
  isCollapsed,
  isExpanded,
  onToggle,
  onContextMenu
}: {
  folder: FileItem;
  isCollapsed: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onContextMenu: (e: React.MouseEvent, item?: FileItem) => void;
}) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      className={cn(
        "fileexplorer-folder-button flex items-center gap-2 px-2 py-1.5 w-full hover:bg-gray-200 rounded-none",
        isCollapsed && "justify-center"
      )}
      title={folder.name}
      onClick={onToggle}
      onContextMenu={(e) => onContextMenu(e, folder)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-expanded={isExpanded}
    >
      {isExpanded ? (
        <ChevronDown className="fileexplorer-icon" />
      ) : (
        <ChevronRight className="fileexplorer-icon" />
      )}
      {!isCollapsed && <span className="truncate">{folder.name}</span>}
    </div>
  );
});

FolderItemComponent.displayName = 'FolderItemComponent';

// Calculate indentation class based on level, but return no indentation if sidebar is collapsed
const getIndentClass = (level: number, isCollapsed: boolean) => {
  if (isCollapsed) return '';

  switch (level) {
    case 1: return 'ml-4';
    case 2: return 'ml-8';
    case 3: return 'ml-12';
    case 4: return 'ml-16';
    case 5: return 'ml-20';
    default: return 'ml-24';
  }
};

// Recursively render folder structure in a flat list
const FileExplorerList = memo(({
  items,
  isCollapsed,
  activeFilePath,
  onOpen,
  level = 0,
  expandedFolders,
  toggleFolder,
  onContextMenu
}: {
  items: FileItem[];
  isCollapsed: boolean;
  activeFilePath?: string | null;
  onOpen: (file: FileItem) => void;
  level?: number;
  expandedFolders: Set<string>;
  toggleFolder: (path: string) => void;
  onContextMenu: (e: React.MouseEvent, item?: FileItem) => void;
}) => {
  // Prepare items for potential virtualization in the future
  const renderableItems = useMemo(() => {
    return items.map(item => ({
      item,
      id: item.path,
      isFolder: item.type === "folder",
      hasContents: item.type === "folder" && item.contents && item.contents.length > 0,
      isExpanded: item.type === "folder" && expandedFolders.has(item.path),
      isActive: item.type === "file" && activeFilePath === item.path
    }));
  }, [items, expandedFolders, activeFilePath]);

  return (
    <>
      {renderableItems.map(({ item, id, isFolder, hasContents, isExpanded, isActive }) => (
        <div key={id} className="mb-1">
          {isFolder ? (
            <>
              <FolderItemComponent
                folder={item}
                isCollapsed={isCollapsed}
                isExpanded={isExpanded}
                onToggle={() => toggleFolder(item.path)}
                onContextMenu={onContextMenu}
              />
              {isExpanded && hasContents && (
                <div className={getIndentClass(level + 1, isCollapsed)}>
                  <FileExplorerList
                    key={`contents-${id}`}
                    items={item.contents!}
                    isCollapsed={isCollapsed}
                    activeFilePath={activeFilePath}
                    onOpen={onOpen}
                    level={level + 1}
                    expandedFolders={expandedFolders}
                    toggleFolder={toggleFolder}
                    onContextMenu={onContextMenu}
                  />
                </div>
              )}
            </>
          ) : (
            <FileItemComponent
              file={item}
              isCollapsed={isCollapsed}
              isActive={isActive}
              onOpen={onOpen}
              onContextMenu={onContextMenu}
            />
          )}
        </div>
      ))}
    </>
  );
});

FileExplorerList.displayName = 'FileExplorerList';

// Context Menu Component
const ContextMenu = memo(({
  x,
  y,
  onClose,
  onUpload,
  onDelete,
  showDelete
}: {
  x: number;
  y: number;
  onClose: () => void;
  onUpload: () => void;
  onDelete?: () => void;
  showDelete?: boolean;
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="fixed z-[100] bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[150px]"
      style={{
        top: `${y}px`,
        left: `${x}px`,
        maxWidth: 'calc(100% - 20px)', // Prevent menu from extending beyond container
        maxHeight: 'calc(100% - 20px)', // Prevent menu from extending beyond container
        overflow: 'auto' // Add scrolling if needed
      }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onUpload();
          onClose();
        }}
        className="w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
      >
        <Upload className="h-3.5 w-3.5" />
        Upload Files
      </button>
      {showDelete && onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
            onClose();
          }}
          className="w-full px-4 py-2 text-xs text-left text-red-700 hover:bg-red-50 transition-colors flex items-center gap-2"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete File
        </button>
      )}
    </div>
  );
});

ContextMenu.displayName = 'ContextMenu';

const FileExplorer: FC<FileExplorerProps> = ({
  files,
  isCollapsed,
  onItemClick,
  activeFilePath,
  onUploadFiles,
  onFilesUpdated
}) => {

  // State to track expanded folders
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // State for context menu
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextMenuTarget, setContextMenuTarget] = useState<FileItem | null>(null);

  // State for drag and drop
  const [isDragging, setIsDragging] = useState(false);

  // Filter out hidden items - memoize the filtering function
  const filterItems = useCallback((items: FileItem[]): FileItem[] => {
    return items
      .filter(item => !shouldHideFile(item.name))
      .map(item => {
        if (item.type === 'folder' && item.contents) {
          return {
            ...item,
            contents: filterItems(item.contents)
          };
        }
        return item;
      });
  }, []);

  // Memoize filtered files
  const filteredFiles = useMemo(() => {
    const filtered = filterItems(files);
    return filtered;
  }, [files, filterItems]);

  useEffect(() => {

    const collectFolderPaths = (items: FileItem[], acc: Set<string>) => {
      items.forEach(item => {
        if (item.type === 'folder') {
          acc.add(item.path);
          if (item.contents) collectFolderPaths(item.contents, acc);
        }
      });
    };

    const availableFolders = new Set<string>();
    collectFolderPaths(filteredFiles, availableFolders);

    setExpandedFolders(prev => {
      const next = new Set<string>();

      availableFolders.forEach(path => {
        if (prev.has(path) || prev.size === 0) {
          next.add(path);
        }
      });

      if (next.size !== prev.size || Array.from(next).some(p => !prev.has(p))) {
        return next;
      }
      return prev;
    });
  }, [filteredFiles]);

  // Ensure folders containing the active file are expanded
  useEffect(() => {
    if (!activeFilePath) return;

    // Function to expand parent folders of a file
    const expandParentFolders = () => {
      const pathParts = activeFilePath.split('/');
      let currentPath = '';

      // Build all parent folder paths and add them to expanded folders
      const newExpandedFolders = new Set(expandedFolders);

      for (let i = 1; i < pathParts.length - 1; i++) {
        currentPath = currentPath ? `${currentPath}/${pathParts[i]}` : `/${pathParts[i]}`;
        newExpandedFolders.add(currentPath);
      }

      // Only update state if there are changes
      if (newExpandedFolders.size !== expandedFolders.size) {
        setExpandedFolders(newExpandedFolders);
      }
    };

    expandParentFolders();
  }, [activeFilePath, expandedFolders]);

  // Toggle folder expanded state - optimized to prevent unnecessary re-renders
  const toggleFolder = useCallback((folderPath: string) => {
    setExpandedFolders(prev => {
      // If state won't change, return the same object to prevent re-renders
      if ((prev.has(folderPath) && prev.size === 1) ||
        (!prev.has(folderPath) && prev.size === 0)) {
        return prev;
      }

      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  }, []);

  // Memoize event handlers to prevent recreating them on each render
  const handleFileOpen = useCallback((file: FileItem) => {
    onItemClick(file.path, "file");
  }, [onItemClick]);

  // Handle context menu
  const handleContextMenu = useCallback((e: React.MouseEvent, item?: FileItem) => {
    e.preventDefault();
    e.stopPropagation();

    // Calculate position relative to viewport
    const viewportX = e.clientX;
    const viewportY = e.clientY;

    // Set position with adjustments to keep menu in viewport
    setContextMenuPosition({
      x: viewportX,
      y: viewportY
    });
    setContextMenuTarget(item || null);
    setShowContextMenu(true);
  }, []);

  const handleDeleteFile = useCallback(async () => {
    if (!contextMenuTarget || contextMenuTarget.type !== 'file') return;

    const fileName = contextMenuTarget.name;
    const filePath = contextMenuTarget.path;

    const confirmDelete = window.confirm(`Are you sure you want to delete "${fileName}"? This action cannot be undone.`);

    if (!confirmDelete) return;

    try {
      await filesystemService.deleteFileOrDirectory(filePath);

      if (onFilesUpdated) {
        onFilesUpdated();
      }

      console.log(`Successfully deleted file: ${filePath}`);
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file. Please try again.');
    }
  }, [contextMenuTarget, onFilesUpdated]);

  const canDeleteFile = useCallback((item: FileItem | null): boolean => {
    if (!item || item.type !== 'file') return false;

    return item.path.startsWith('/project/data/');
  }, []);

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Drag enter event");
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set isDragging to false if we're leaving the container itself
    // This prevents flickering when dragging over child elements
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }

    console.log("Drag leave event");
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Drag over event");
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Drop event");
    setIsDragging(false);

    // If onUploadFiles is provided, call it to trigger file upload
    if (onUploadFiles) {
      console.log("Calling onUploadFiles with drag event");
      // Create a new DragEvent from the React.DragEvent
      const dragEvent = new DragEvent('drop', {
        bubbles: true,
        cancelable: true,
        dataTransfer: e.dataTransfer
      });

      // Pass the drag event to the parent component
      onUploadFiles(dragEvent);
    }
  }, [onUploadFiles]);

  // For large file structures, the component is ready to implement virtualization
  return (
    <div
      className={cn(
        "fileexplorer-container relative h-full",
        isDragging && "bg-gray-50 border-2 border-dashed border-gray-300"
      )}
      onContextMenu={(e) => handleContextMenu(e)}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-80 z-10">
          <div className="text-center p-4">
            <Upload className="h-6 w-6 mx-auto text-gray-500 mb-2" />
            <p className="text-sm font-medium text-gray-600">Drop files to upload</p>
          </div>
        </div>
      )}

      {filteredFiles.length > 0 ? (
        <FileExplorerList
          items={filteredFiles}
          isCollapsed={isCollapsed}
          activeFilePath={activeFilePath}
          onOpen={handleFileOpen}
          expandedFolders={expandedFolders}
          toggleFolder={toggleFolder}
          onContextMenu={handleContextMenu}
        />
      ) : (
        <div className="py-2 px-3 text-sm text-muted-foreground">
          No files found
        </div>
      )}

      {showContextMenu && onUploadFiles && (
        <ContextMenu
          x={contextMenuPosition.x}
          y={contextMenuPosition.y}
          onClose={() => setShowContextMenu(false)}
          onUpload={onUploadFiles}
          onDelete={handleDeleteFile}
          showDelete={canDeleteFile(contextMenuTarget)}
        />
      )}
    </div>
  );
};

export default FileExplorer;